import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { TextField, InputAdornment, IconButton, Popover } from '@material-ui/core';
import CalendarToday from '@material-ui/icons/CalendarToday';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import moment from 'moment';

// Text positions of the six editable digits in "DD/MM/20YY"
const slots = [0, 1, 3, 4, 8, 9];
const maskLength = 10;
const blank = '______';
const minDate = new Date(2000, 0, 1);
const maxDate = new Date(2099, 11, 31);

// chars holds the six digits with "_" for the ones not entered yet
function format(chars) {
    return `${chars.substring(0, 2)}/${chars.substring(2, 4)}/20${chars.substring(4)}`;
}

function isComplete(chars) {
    return chars.indexOf('_') < 0;
}

function toMoment(chars) {
    if (!isComplete(chars)) return null;
    const m = moment(chars.substring(0, 4) + '20' + chars.substring(4), 'DDMMYYYY', true);
    return m.isValid() ? m : null;
}

// Accepts "YYYY-MM-DD" or an ISO datetime string from the API
function normalize(value) {
    if (!value) return '';
    const m = moment(String(value).substring(0, 10), 'YYYY-MM-DD', true);
    return m.isValid() && m.year() >= 2000 && m.year() <= 2099 ? m.format('YYYY-MM-DD') : '';
}

function toChars(value) {
    const iso = normalize(value);
    return iso ? moment(iso).format('DDMMYY') : blank;
}

function pastedChars(text) {
    const iso = text.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return iso[3] + iso[2] + iso[1].substring(2);
    let digits = text.replace(/\D/g, '');
    if (digits.length === 8) digits = digits.substring(0, 4) + digits.substring(6);
    return (digits + blank).substring(0, slots.length);
}

// Index of the first slot at or after a text position, or slots.length past the last one
function slotAt(pos) {
    const i = slots.findIndex((x) => x >= pos);
    return i < 0 ? slots.length : i;
}

function caretAt(i) {
    return i < slots.length ? slots[i] : maskLength;
}

// Blanks the slots inside a text selection
function clearRange(chars, start, end) {
    return chars.split('').map((c, i) => (slots[i] >= start && slots[i] < end ? '_' : c)).join('');
}

// Writes digits from the caret onwards, overwriting what is there.
// A caret past the entered digits snaps back to the first empty slot.
function insertDigits(chars, pos, digits) {
    let i = slotAt(pos);
    const firstBlank = chars.indexOf('_');
    if (firstBlank >= 0 && firstBlank < i && chars.substring(firstBlank).replace(/_/g, '') === '') i = firstBlank;
    const result = chars.split('');
    for (const d of digits) {
        if (i >= slots.length) break;
        result[i++] = d;
    }
    return { chars: result.join(''), caret: caretAt(i) };
}

// Masked DD/MM/20YY date input with a calendar picker. value and onChange use "YYYY-MM-DD",
// and onChange receives "" while the date is empty, incomplete or invalid.
function DateField({ value, onChange, onKeyDown, onFocus, onBlur, error, helperText, InputLabelProps, InputProps, inputProps, ...props }) {
    const [chars, setChars] = useState(() => toChars(value));
    const [caret, setCaret] = useState(null);
    const [focused, setFocused] = useState(false);
    const [calendarAnchor, setCalendarAnchor] = useState(null);
    const inputRef = useRef(null);
    const emitted = useRef(normalize(value));

    // Pick up dates set by the parent, such as when a record is loaded for editing
    useEffect(() => {
        const iso = normalize(value);
        if (iso !== emitted.current) {
            emitted.current = iso;
            setChars(toChars(value));
        }
    }, [value]);

    // Restore the caret after React re-renders the masked text
    useLayoutEffect(() => {
        const input = inputRef.current;
        if (caret && input && document.activeElement === input) input.setSelectionRange(caret.pos, caret.pos);
    }, [caret]);

    function update(newChars, caretPos) {
        setChars(newChars);
        setCaret({ pos: caretPos });
        const m = toMoment(newChars);
        const iso = m ? m.format('YYYY-MM-DD') : '';
        if (iso !== emitted.current) {
            emitted.current = iso;
            if (onChange) onChange(iso);
        }
    }

    function handleKeyDown(e) {
        if (onKeyDown) onKeyDown(e);
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        const { selectionStart: start, selectionEnd: end } = e.target;
        if (/^\d$/.test(e.key)) {
            e.preventDefault();
            const result = insertDigits(clearRange(chars, start, end), start, e.key);
            update(result.chars, result.caret);
        } else if (e.key === 'Backspace') {
            e.preventDefault();
            if (end > start) {
                update(clearRange(chars, start, end), caretAt(slotAt(start)));
            } else {
                const i = slotAt(start) - 1;
                if (i >= 0) update(clearRange(chars, slots[i], slots[i] + 1), slots[i]);
            }
        } else if (e.key === 'Delete') {
            e.preventDefault();
            if (end > start) update(clearRange(chars, start, end), caretAt(slotAt(start)));
            else update(clearRange(chars, start, caretAt(slotAt(start)) + 1), start);
        } else if (e.key.length === 1) {
            e.preventDefault();
        }
    }

    // Fallback for virtual keyboards that don't report keys on keydown
    function handleChange(e) {
        const text = e.target.value;
        const pos = e.target.selectionStart;
        const diff = text.length - maskLength;
        if (diff < 0) {
            update(clearRange(chars, pos, pos - diff), pos);
        } else if (diff > 0) {
            const inserted = text.substring(pos - diff, pos).replace(/\D/g, '');
            const result = insertDigits(chars, pos - diff, inserted);
            update(result.chars, result.caret);
        } else {
            update(chars, pos);
        }
    }

    function handlePaste(e) {
        e.preventDefault();
        const newChars = pastedChars(e.clipboardData.getData('text'));
        update(newChars, caretAt(isComplete(newChars) ? slots.length : newChars.indexOf('_')));
    }

    function handleFocus(e) {
        setFocused(true);
        const input = e.target;
        // A complete date is selected so typing replaces it, otherwise continue where it was left
        setTimeout(() => {
            if (document.activeElement !== input) return;
            if (isComplete(chars)) input.setSelectionRange(0, maskLength);
            else input.setSelectionRange(caretAt(chars.indexOf('_')), caretAt(chars.indexOf('_')));
        });
        if (onFocus) onFocus(e);
    }

    function handleBlur(e) {
        setFocused(false);
        if (onBlur) onBlur(e);
    }

    function handleCalendarChange(date) {
        setCalendarAnchor(null);
        if (inputRef.current) inputRef.current.focus();
        update(moment(date).format('DDMMYY'), maskLength);
    }

    let message = '';
    if (isComplete(chars) && !toMoment(chars)) message = 'Invalid date';
    else if (!isComplete(chars) && chars !== blank && !focused) message = 'Incomplete date';

    const selected = toMoment(chars);

    return (
        <>
            <TextField
                {...props}
                value={format(chars)}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onFocus={handleFocus}
                onBlur={handleBlur}
                error={Boolean(message) || Boolean(error)}
                helperText={message || helperText}
                inputRef={inputRef}
                InputLabelProps={{ ...InputLabelProps, shrink: true }}
                inputProps={{ ...inputProps, inputMode: 'numeric', style: { fontVariantNumeric: 'tabular-nums' } }}
                InputProps={{
                    ...InputProps,
                    // An endAdornment passed in is shown before the calendar button
                    endAdornment: (
                        <InputAdornment position="end">
                            {InputProps && InputProps.endAdornment}
                            <IconButton
                                size="small"
                                edge="end"
                                tabIndex={-1}
                                aria-label="Pick date from calendar"
                                disabled={props.disabled}
                                onClick={(e) => setCalendarAnchor(e.currentTarget)}
                            >
                                <CalendarToday fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
            <Popover
                open={Boolean(calendarAnchor)}
                anchorEl={calendarAnchor}
                onClose={() => setCalendarAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Calendar
                    value={selected ? selected.toDate() : null}
                    minDate={minDate}
                    maxDate={maxDate}
                    onChange={handleCalendarChange}
                />
            </Popover>
        </>
    );
}

export default DateField;
